"use client";

import { Fragment, type CSSProperties, useEffect, useState } from "react";
import Link from "next/link";
import { motion, AnimatePresence, useReducedMotion } from "@/lib/motion";
import { cn } from "@/lib/utils";
import { SectionHeader, StarRating, PlatformLogo, buttonVariants } from "@/components/ui/";
import { ArrowRight } from "@/components/icons";
import { reviews, REGISTER_URL, G2_URL, TRUSTPILOT_URL } from "@/lib/constants";
import type { ReviewsSectionBaseProps } from "./types";

type Review = (typeof reviews)[number];

/* Two initials for the monogram avatar, e.g. "Bryant A." → "BA". */
function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("");
}

function Avatar({ name, className }: { name: string; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "text-brand-green flex shrink-0 items-center justify-center rounded-full font-semibold",
        className
      )}
      style={{ backgroundColor: "color-mix(in srgb, var(--brand-green) 12%, transparent)" }}
    >
      {initials(name)}
    </span>
  );
}

function ReviewCard({ review }: { review: Review }) {
  return (
    <figure className="bg-card border-border rounded-2xl border p-5">
      <div className="mb-3 flex items-center justify-between">
        <StarRating rating={review.rating} platform={review.platform} size={13} />
        <span className="shrink-0 [&_img]:w-14" aria-hidden="true">
          <PlatformLogo platform={review.platform} />
        </span>
      </div>
      <blockquote className="text-muted-foreground line-clamp-6 text-[13.5px] leading-relaxed">
        &ldquo;{review.quote}&rdquo;
      </blockquote>
      <figcaption className="mt-4 flex items-center gap-2.5">
        <Avatar name={review.author} className="h-8 w-8 text-[12px]" />
        <span className="text-foreground text-[13px] font-semibold">{review.author}</span>
      </figcaption>
    </figure>
  );
}

/** One auto-scrolling column. The track duplicates the list so the CSS marquee
    (see globals.css) loops seamlessly; hovering the column pauses it. */
function ReviewColumn({
  items,
  reverse,
  durationSec,
  className,
}: {
  items: Review[];
  reverse?: boolean;
  durationSec: number;
  className?: string;
}) {
  return (
    <div className={cn("reviews-col", className)}>
      <div
        className={cn("reviews-track flex flex-col gap-4", reverse && "is-reverse")}
        style={{ "--reviews-dur": `${durationSec}s` } as CSSProperties}
      >
        {["a", "b"].map((copy) => (
          <Fragment key={copy}>
            {items.map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

// Soft top/bottom fade so cards dissolve into the band rather than clip hard.
const FADE_MASK = "linear-gradient(to bottom, transparent, #000 12%, #000 88%, transparent)";

export default function ReviewsSectionBase({
  heading,
  headingId,
  eyebrow = "Customer Reviews",
  body,
  excludeProduct,
}: ReviewsSectionBaseProps) {
  // A product page only shows reviews that aren't tagged for the other product
  // line, so the residential page never surfaces a mobile-specific quote.
  const visibleReviews = excludeProduct
    ? reviews.filter((r) => r.product !== excludeProduct)
    : reviews;

  // The featured quote rotates through the more detailed reviews so it fills the
  // fixed-height slot consistently; it holds still under reduced motion.
  const reduced = useReducedMotion();
  const featuredPool = visibleReviews.filter((r) => r.quote.length > 180);
  const [featuredIdx, setFeaturedIdx] = useState(0);

  useEffect(() => {
    if (reduced || featuredPool.length <= 1) return;
    const t = setInterval(() => setFeaturedIdx((n) => (n + 1) % featuredPool.length), 5200);
    return () => clearInterval(t);
  }, [reduced, featuredPool.length]);
  const featured = featuredPool[featuredIdx];

  const colA = visibleReviews.filter((_, i) => i % 2 === 0);
  const colB = visibleReviews.filter((_, i) => i % 2 === 1);

  return (
    <section
      /* No band tint: --card resolves through --surface-1, which is pure white
         on the light theme, so a tinted band would render white cards on a
         white section. The full-bleed border-y still marks the section. */
      className="full-bleed border-border bg-band py-section border-y"
      aria-labelledby={headingId}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-12">
          {/* Left — pitch, CTA, featured quote */}
          <div className="lg:col-span-5 lg:self-center">
            <SectionHeader
              eyebrow={eyebrow}
              id={headingId}
              heading={heading}
              headingClassName="mb-2"
              body={body}
              bodyClassName="text-base leading-normal"
            />

            <Link
              href={REGISTER_URL}
              className={cn(buttonVariants({ variant: "default", size: "lg" }), "group/cta mt-7")}
            >
              Sign up for free
              <ArrowRight
                size={16}
                aria-hidden="true"
                className="transition-transform duration-200 group-hover/cta:translate-x-0.5"
              />
            </Link>

            <p className="text-muted-foreground mt-3 text-sm">
              Leave a review on{" "}
              <a
                href={G2_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-foreground hover:text-brand-green font-medium underline underline-offset-2"
              >
                G2
              </a>{" "}
              or{" "}
              <a
                href={TRUSTPILOT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-foreground hover:text-brand-green font-medium underline underline-offset-2"
              >
                Trustpilot
              </a>
            </p>

            <figure className="border-border mt-10 border-t pt-8">
              <span
                aria-hidden="true"
                className="text-brand-green/25 block font-serif text-5xl leading-none"
              >
                &ldquo;
              </span>
              {/* Fixed height + clipped so each quote fades in drifting from the
                  right and out to the left, one at a time (mode="wait" — no
                  overlap), without shifting the layout. */}
              <div className="relative mt-1 min-h-44 overflow-hidden">
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={featured.id}
                    initial={reduced ? false : { opacity: 0, x: 28 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={reduced ? { opacity: 0 } : { opacity: 0, x: -28 }}
                    transition={{ duration: reduced ? 0 : 0.3, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <blockquote className="text-foreground line-clamp-4 text-base leading-relaxed">
                      {featured.quote}
                    </blockquote>
                    <figcaption className="mt-5 flex items-center gap-3">
                      <Avatar name={featured.author} className="h-10 w-10 text-[13px]" />
                      <div>
                        <div className="text-foreground text-sm font-semibold">
                          {featured.author}
                        </div>
                        <div className="text-muted-foreground text-xs">
                          Verified {featured.platform} review
                        </div>
                      </div>
                    </figcaption>
                  </motion.div>
                </AnimatePresence>
              </div>
            </figure>
          </div>

          {/* Right — scrolling review wall */}
          <div className="lg:col-span-7">
            <div
              className="relative h-130 overflow-hidden lg:h-150"
              style={{ maskImage: FADE_MASK, WebkitMaskImage: FADE_MASK }}
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <ReviewColumn items={colA} durationSec={48} />
                <ReviewColumn items={colB} reverse durationSec={56} className="hidden sm:block" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
